import React, { createContext, useContext, useEffect, useCallback, useMemo } from 'react';
import { 
  getLeaderboardStats, 
  getTopGoalScorers, 
  getTopAssistLeaders, 
  getTopPassLeaders, 
  getTopInterceptionLeaders, 
  getTopSaveLeaders, 
  getTopTeams,
  getGlobalMVPLeaderboard 
} from '../services/database';
import { useBaseContext, createBaseState } from './shared/useBaseContext';
import { enhancedSubscriptionManager } from './shared/enhancedSubscriptionManager';
import { createErrorHandler, handleAsyncOperation } from './shared/errorHandler';

const LeaderboardContext = createContext();

// Leaderboard-specific actions
const leaderboardActions = {
  SET_LEADERBOARDS: 'SET_LEADERBOARDS',
  SET_TOP_GOAL_SCORERS: 'SET_TOP_GOAL_SCORERS',
  SET_TOP_ASSIST_LEADERS: 'SET_TOP_ASSIST_LEADERS',
  SET_TOP_PASS_LEADERS: 'SET_TOP_PASS_LEADERS',
  SET_TOP_INTERCEPTION_LEADERS: 'SET_TOP_INTERCEPTION_LEADERS',
  SET_TOP_SAVE_LEADERS: 'SET_TOP_SAVE_LEADERS',
  SET_TOP_TEAMS: 'SET_TOP_TEAMS',
  SET_MVP_LEADERS: 'SET_MVP_LEADERS'
};

// Leaderboard-specific reducer
const leaderboardReducer = (state, action) => {
  switch (action.type) {
    case leaderboardActions.SET_LEADERBOARDS:
      return {
        ...state,
        ...action.payload,
        loading: false,
        error: null
      };
    case leaderboardActions.SET_TOP_GOAL_SCORERS:
      return { ...state, topGoalScorers: action.payload };
    case leaderboardActions.SET_TOP_ASSIST_LEADERS:
      return { ...state, topAssistLeaders: action.payload };
    case leaderboardActions.SET_TOP_PASS_LEADERS:
      return { ...state, topPassLeaders: action.payload };
    case leaderboardActions.SET_TOP_INTERCEPTION_LEADERS:
      return { ...state, topInterceptionLeaders: action.payload };
    case leaderboardActions.SET_TOP_SAVE_LEADERS:
      return { ...state, topSaveLeaders: action.payload };
    case leaderboardActions.SET_TOP_TEAMS:
      return { ...state, topTeams: action.payload };
    case leaderboardActions.SET_MVP_LEADERS:
      return { ...state, mvpLeaders: action.payload };
    default:
      return state;
  }
};

const initialState = createBaseState({
  players: [],
  teams: [],
  topGoalScorers: [],
  topAssistLeaders: [],
  topPassLeaders: [],
  topInterceptionLeaders: [],
  topSaveLeaders: [],
  topTeams: [],
  mvpLeaders: []
});

export const LeaderboardProvider = ({ children }) => {
  const { state, dispatch, setLoading, clearError } = useBaseContext(initialState, leaderboardReducer);
  const errorHandler = useMemo(() => createErrorHandler('LEADERBOARD'), []);

  const loadLeaderboards = useCallback(async (limit = 20) => {
    return handleAsyncOperation(async () => {
      setLoading(true);
      clearError();
      
      // Load all leaderboard data concurrently with configurable limit
      const [
        leaderboardResult,
        goalScorersResult,
        assistLeadersResult,
        passLeadersResult,
        interceptionLeadersResult,
        saveLeadersResult,
        topTeamsResult,
        mvpLeadersResult
      ] = await Promise.all([
        getLeaderboardStats(),
        getTopGoalScorers(limit),
        getTopAssistLeaders(limit),
        getTopPassLeaders(limit),
        getTopInterceptionLeaders(limit),
        getTopSaveLeaders(limit),
        getTopTeams(limit),
        getGlobalMVPLeaderboard(limit)
      ]);

      if (leaderboardResult.success) {
        dispatch({
          type: leaderboardActions.SET_LEADERBOARDS,
          payload: leaderboardResult.data
        });
      }

      if (goalScorersResult.success) {
        dispatch({ type: leaderboardActions.SET_TOP_GOAL_SCORERS, payload: goalScorersResult.data });
      }

      if (assistLeadersResult.success) {
        dispatch({ type: leaderboardActions.SET_TOP_ASSIST_LEADERS, payload: assistLeadersResult.data });
      }

      if (passLeadersResult.success) {
        dispatch({ type: leaderboardActions.SET_TOP_PASS_LEADERS, payload: passLeadersResult.data });
      }

      if (interceptionLeadersResult.success) {
        dispatch({ type: leaderboardActions.SET_TOP_INTERCEPTION_LEADERS, payload: interceptionLeadersResult.data });
      }

      if (saveLeadersResult.success) {
        dispatch({ type: leaderboardActions.SET_TOP_SAVE_LEADERS, payload: saveLeadersResult.data });
      }

      if (topTeamsResult.success) {
        dispatch({ type: leaderboardActions.SET_TOP_TEAMS, payload: topTeamsResult.data });
      }

      if (mvpLeadersResult.success) {
        dispatch({ type: leaderboardActions.SET_MVP_LEADERS, payload: mvpLeadersResult.data });
      }

      return leaderboardResult;
    }, errorHandler);
  }, [setLoading, clearError, dispatch, errorHandler]);

  const loadTopGoalScorers = useCallback(async (limit = 10) => {
    return handleAsyncOperation(async () => {
      const result = await getTopGoalScorers(limit);
      if (result.success) {
        dispatch({ type: leaderboardActions.SET_TOP_GOAL_SCORERS, payload: result.data });
      }
      return result;
    }, errorHandler);
  }, [dispatch, errorHandler]);

  const loadTopAssistLeaders = useCallback(async (limit = 10) => {
    return handleAsyncOperation(async () => {
      const result = await getTopAssistLeaders(limit);
      if (result.success) {
        dispatch({ type: leaderboardActions.SET_TOP_ASSIST_LEADERS, payload: result.data });
      }
      return result;
    }, errorHandler);
  }, [dispatch, errorHandler]);

  const loadTopTeams = useCallback(async (limit = 10) => {
    return handleAsyncOperation(async () => {
      const result = await getTopTeams(limit);
      if (result.success) {
        dispatch({ type: leaderboardActions.SET_TOP_TEAMS, payload: result.data });
      }
      return result;
    }, errorHandler);
  }, [dispatch, errorHandler]);

  const loadMVPLeaders = useCallback(async (limit = 20) => {
    return handleAsyncOperation(async () => {
      const result = await getGlobalMVPLeaderboard(limit);
      if (result.success) {
        dispatch({ type: leaderboardActions.SET_MVP_LEADERS, payload: result.data });
      }
      return result;
    }, errorHandler);
  }, [dispatch, errorHandler]);

  const loadTopPassLeaders = useCallback(async (limit = 10) => {
    return handleAsyncOperation(async () => {
      const result = await getTopPassLeaders(limit);
      if (result.success) {
        dispatch({ type: leaderboardActions.SET_TOP_PASS_LEADERS, payload: result.data });
      }
      return result;
    }, errorHandler);
  }, [dispatch, errorHandler]);

  const loadTopInterceptionLeaders = useCallback(async (limit = 10) => {
    return handleAsyncOperation(async () => {
      const result = await getTopInterceptionLeaders(limit);
      if (result.success) {
        dispatch({ type: leaderboardActions.SET_TOP_INTERCEPTION_LEADERS, payload: result.data });
      }
      return result;
    }, errorHandler);
  }, [dispatch, errorHandler]);

  const loadTopSaveLeaders = useCallback(async (limit = 10) => {
    return handleAsyncOperation(async () => {
      const result = await getTopSaveLeaders(limit);
      if (result.success) {
        dispatch({ type: leaderboardActions.SET_TOP_SAVE_LEADERS, payload: result.data });
      }
      return result;
    }, errorHandler);
  }, [dispatch, errorHandler]);

  useEffect(() => {
    loadLeaderboards();

    // Set up real-time subscriptions with rate limiting
    const subscriptionConfigs = [
      { 
        event: { event: '*', schema: 'public', table: 'player_match_stats' },
        rateLimitMs: 20000 // 20 second rate limit for leaderboard stats
      },
      { 
        event: { event: '*', schema: 'public', table: 'matches' },
        rateLimitMs: 15000 // 15 second rate limit for match updates
      },
      { 
        event: { event: '*', schema: 'public', table: 'teams' },
        rateLimitMs: 30000 // 30 second rate limit for team updates
      }
    ];

    enhancedSubscriptionManager.subscribeMultiple(
      'leaderboard_changes',
      subscriptionConfigs,
      () => loadLeaderboards()
    );

    return () => {
      enhancedSubscriptionManager.unsubscribe('leaderboard_changes');
    };
  }, [loadLeaderboards]);

  const getPlayerRank = useCallback((playerId, category = 'goals') => {
    const players = category === 'goals' ? state.topGoalScorers : state.topAssistLeaders;
    const rank = players.findIndex(player => player.player_id === playerId) + 1;
    return rank > 0 ? rank : null;
  }, [state.topGoalScorers, state.topAssistLeaders]);

  const getTeamRank = useCallback((teamId) => {
    const rank = state.topTeams.findIndex(team => team.team_id === teamId) + 1;
    return rank > 0 ? rank : null;
  }, [state.topTeams]);

  const refreshLeaderboards = useCallback(() => {
    loadLeaderboards();
  }, [loadLeaderboards]);

  const value = {
    ...state,
    loadLeaderboards,
    loadTopGoalScorers,
    loadTopAssistLeaders,
    loadTopPassLeaders,
    loadTopInterceptionLeaders,
    loadTopSaveLeaders,
    loadTopTeams,
    loadMVPLeaders,
    getPlayerRank,
    getTeamRank,
    clearError,
    refreshLeaderboards
  };

  return (
    <LeaderboardContext.Provider value={value}>
      {children}
    </LeaderboardContext.Provider>
  );
};

export const useLeaderboards = () => {
  const context = useContext(LeaderboardContext);
  if (!context) {
    throw new Error('useLeaderboards must be used within a LeaderboardProvider');
  }
  return context;
};

export default LeaderboardContext;
